import { TweenHost } from './TweenHost';
import { FramePhaseBudget } from '../diagnostics/FramePhaseBudget';

/**
 * TweenHostDriver — rAF-based driver for the TweenHost.
 *
 * Phase H10 Arc β (2026-05-08): owns the per-frame `step(deltaMs)`
 * call into TweenHost. Started once at module load (via the
 * `start()` call at the bottom of this file), runs forever in
 * the browser, no-op'd under SSR / non-window environments.
 *
 * Why a dedicated driver vs piggybacking on PixiOverlayHost.app.ticker:
 *   - Some surfaces still run on the Phaser canvas today; they don't
 *     have a Pixi ticker available.
 *   - The TweenHost is substrate-agnostic; its driver should be too.
 *   - Once the GameHost (Arc ι) lands as the single tick source, the
 *     standalone rAF here can be retired in favor of the host's tick.
 *
 * The driver clamps delta to [0, 100ms] to keep tweens sane after a
 * tab-switch or long stall (large dt would teleport tweens to their
 * end values — the clamp limits that to one frame's worth of budget).
 */

let started = false;
let lastTimeMs = 0;
let rafId: number | null = null;

const CLAMP_MAX_DT_MS = 100;

function tick(nowMs: number): void {
  if (!started) return;
  const deltaMs = lastTimeMs === 0 ? 0 : Math.min(nowMs - lastTimeMs, CLAMP_MAX_DT_MS);
  lastTimeMs = nowMs;
  const t0 = performance.now();
  TweenHost.step(deltaMs);
  FramePhaseBudget.record('tween', performance.now() - t0);
  rafId = requestAnimationFrame(tick);
}

/** Kick off the rAF loop. Idempotent — safe to call multiple times. */
export function start(): void {
  if (started) return;
  if (typeof requestAnimationFrame !== 'function') return; // SSR / tests
  started = true;
  lastTimeMs = 0;
  rafId = requestAnimationFrame(tick);
}

/** Stop the rAF loop. Used in tests + on full game-host teardown. */
export function stop(): void {
  started = false;
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  lastTimeMs = 0;
}

// Boot-time concern: the game's bootstrap layer (GameBootstrap /
// future GameBootstrap) calls start() explicitly. Auto-starting here
// would leak rAF callbacks into jsdom test runs, so we keep it
// opt-in.
